import React, { useEffect, useRef, useState } from "react";

export type SortDir = 1 | -1;

/** Generic array sort: case-insensitive strings, nulls/undefined always last. */
export function sortItems<T extends Record<string, any>>(items: T[], key: string | null, dir: SortDir): T[] {
  if (!key) return items;
  return [...items].sort((a, b) => {
    let av: any = a[key];
    let bv: any = b[key];
    if (av === null || av === undefined || av === "") av = null;
    if (bv === null || bv === undefined || bv === "") bv = null;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

/** Click-to-sort state: click toggles asc/desc, clicking a new column resets to asc. */
export function useSortState() {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(1);

  function onSort(key: string | null) {
    if (!key) return;
    setSortKey((cur) => {
      if (cur === key) {
        setSortDir((d) => (-d as SortDir));
        return cur;
      }
      setSortDir(1);
      return key;
    });
  }

  function clearSort() {
    setSortKey(null);
    setSortDir(1);
  }

  return { sortKey, sortDir, onSort, clearSort };
}

interface SortThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  columnKey?: string | null;
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
}

/** Sortable <th> — pass columnKey=null for non-sortable columns (e.g. action columns). */
export function SortTh({ children, columnKey, sortKey, sortDir, onSort, ...rest }: SortThProps) {
  if (!columnKey) return <th {...rest}>{children}</th>;
  const active = sortKey === columnKey;
  const cls = ["col-sortable", active ? (sortDir === 1 ? "sort-asc" : "sort-desc") : ""].join(" ").trim();
  return (
    <th className={cls} onClick={() => onSort(columnKey)} {...rest}>
      {children}
    </th>
  );
}

/** Show/hide-columns state, keyed by each column's unique `key`. */
export function useColumnVisibility(allKeys: string[]) {
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());

  function toggle(key: string) {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function selectAll(checked: boolean) {
    setHidden(checked ? new Set() : new Set(allKeys));
  }

  return { hidden, toggle, selectAll, allChecked: hidden.size === 0 };
}

export interface ColumnDef {
  key: string;
  label: string;
  [extra: string]: any;
}

interface ColumnMenuProps {
  columns: ColumnDef[];
  hidden: Set<string>;
  toggle: (key: string) => void;
  selectAll: (checked: boolean) => void;
  allChecked: boolean;
}

/** "⚙ Columns" button + checkbox dropdown panel, closes on outside click. */
export function ColumnMenu({ columns, hidden, toggle, selectAll, allChecked }: ColumnMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <div className="col-menu" ref={ref}>
      <button type="button" className="col-menu-btn" onClick={() => setOpen((o) => !o)}>
        &#9881; Columns
      </button>
      <div className={`col-menu-panel ${open ? "open" : ""}`}>
        <label className="col-menu-select-all">
          <input type="checkbox" checked={allChecked} onChange={(e) => selectAll(e.target.checked)} /> Select All
        </label>
        {columns.map((c) => (
          <label key={c.key}>
            <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggle(c.key)} /> {c.label}
          </label>
        ))}
      </div>
    </div>
  );
}
